import { NavLink, Outlet } from "react-router-dom";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../modules/auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { apiGet, apiLogout, apiPost } from "../shared/apiClient";
import { ConfirmDialogHost } from "../shared/ConfirmDialogHost";
import { useToast } from "../shared/ToastContext";

function IconDashboard({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1.2" />
      <rect x="14" y="3" width="7" height="7" rx="1.2" />
      <rect x="3" y="14" width="7" height="7" rx="1.2" />
      <rect x="14" y="14" width="7" height="7" rx="1.2" />
    </svg>
  );
}

function IconBuilding({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M9 21v-4h6v4" />
      <path d="M9 10h.01" />
      <path d="M15 10h.01" />
    </svg>
  );
}

function IconArticle({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  );
}

function IconActivity({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function IconUsers({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconChat({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
    </svg>
  );
}

const navItemClass = ({ isActive }) =>
  `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm ${
    isActive ? "bg-white/15" : "hover:bg-white/10"
  }`;

const navIconClass = "h-4 w-4 shrink-0 text-white/90";

function playChatTone() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const playBeep = (offset, frequency) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "square";
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + offset);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.24, ctx.currentTime + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + offset + 0.18);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(ctx.currentTime + offset);
      oscillator.stop(ctx.currentTime + offset + 0.2);
    };
    playBeep(0, 1046);
    playBeep(0.24, 1318);
    playBeep(0.48, 1046);
  } catch {
    /* Browsers can block audio until the user interacts with the page. */
  }
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const canManageEmployees = isAdmin || role === "customer";
  const companyOrRole = (user?.customerName && String(user.customerName).trim()) || "Admin";
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [waitingChatCount, setWaitingChatCount] = useState(0);
  const [acceptingChat, setAcceptingChat] = useState(false);
  const previousWaitingChatCountRef = useRef(null);

  const loadWaitingChats = useCallback(async (cancelledRef) => {
    const res = await apiGet("/api/LiveChat/summary");
    if (!cancelledRef.current && res.ok) {
      const nextCount = Number(res.data?.waiting || 0);
      const previousCount = previousWaitingChatCountRef.current;
      if (previousCount !== null && nextCount > previousCount) {
        playChatTone();
      }
      previousWaitingChatCountRef.current = nextCount;
      setWaitingChatCount(nextCount);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const cancelledRef = { current: false };
    void loadWaitingChats(cancelledRef);
    const timer = window.setInterval(() => void loadWaitingChats(cancelledRef), 10000);
    return () => {
      cancelledRef.current = true;
      window.clearInterval(timer);
    };
  }, [user, loadWaitingChats]);

  useEffect(() => {
    if (waitingChatCount <= 0) return undefined;

    playChatTone();
    const timer = window.setInterval(() => {
      playChatTone();
    }, 3500);

    return () => window.clearInterval(timer);
  }, [waitingChatCount]);

  const onLogout = async () => {
    await apiLogout();
    logout();
    navigate("/login", { replace: true });
  };

  const acceptWaitingChat = async () => {
    setAcceptingChat(true);
    const listRes = await apiGet("/api/LiveChat?status=Waiting&limit=1");
    if (!listRes.ok) {
      setAcceptingChat(false);
      toast.push(String(listRes.error || "Failed to load waiting chat"), "error");
      return;
    }

    const firstWaiting = Array.isArray(listRes.data) ? listRes.data[0] : null;
    if (!firstWaiting?.id) {
      setAcceptingChat(false);
      setWaitingChatCount(0);
      navigate("/live-chat");
      return;
    }

    const joinRes = await apiPost(`/api/LiveChat/${firstWaiting.id}/join`, {});
    setAcceptingChat(false);
    if (!joinRes.ok) {
      toast.push(String(joinRes.error || "Failed to accept chat"), "error");
      navigate("/live-chat");
      return;
    }

    toast.push("Chat accepted", "success");
    setWaitingChatCount((count) => Math.max(0, count - 1));
    navigate("/live-chat");
  };

  const openChangePassword = () => {
    setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setChangePasswordOpen(true);
  };

  const runChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordForm.currentPassword.trim() || !passwordForm.newPassword.trim()) {
      toast.push("Current and new password are required", "error");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.push("New password and confirm password do not match", "error");
      return;
    }

    setChangingPassword(true);
    const res = await apiPost("/api/Authentication/change-password", {
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword
    });
    setChangingPassword(false);

    if (!res.ok) {
      toast.push(String(res.error), "error");
      return;
    }

    toast.push("Password changed successfully", "success");
    setChangePasswordOpen(false);
  };

  return (
    <div className="min-h-0 bg-slate-50 text-slate-900">
      <ConfirmDialogHost />
      <aside className="fixed left-0 top-0 z-30 flex h-screen w-64 flex-col overflow-hidden bg-gradient-to-b from-brand-800 to-brand-700 p-4 text-white shadow-lg">
        <div className="mb-5 shrink-0">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.14] via-white/[0.06] to-transparent p-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12)] ring-1 ring-white/5">
            <div className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-emerald-400/25 blur-2xl" aria-hidden />
            <div className="pointer-events-none absolute -bottom-4 -left-4 h-16 w-16 rounded-full bg-teal-900/40 blur-xl" aria-hidden />
            <div className="relative flex items-center gap-3">
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-300 to-emerald-800 text-lg font-black tracking-tight text-white shadow-lg shadow-emerald-950/30 ring-2 ring-white/25"
                aria-hidden
              >
                a
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xl font-bold leading-[1.1] tracking-tight text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.2)]">
                  Ainsoft CMS
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex items-center rounded-md bg-black/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-100/95">
                    {companyOrRole}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <nav className="sidebar-scroll flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto pb-2">
          <NavLink to="/" end className={navItemClass}>
            <IconDashboard className={navIconClass} />
            <span>Dashboard</span>
          </NavLink>
          {isAdmin && (
            <NavLink to="/customers" className={navItemClass}>
              <IconBuilding className={navIconClass} />
              <span>Customers</span>
            </NavLink>
          )}
          {canManageEmployees && (
            <NavLink to="/employees" className={navItemClass}>
              <IconUsers className={navIconClass} />
              <span>Employees</span>
            </NavLink>
          )}
          <NavLink to="/live-chat" className={navItemClass}>
            <IconChat className={navIconClass} />
            <span className="min-w-0 flex-1">Live Chat</span>
            {waitingChatCount > 0 && (
              <span className="rounded-full bg-amber-300 px-2 py-0.5 text-[11px] font-bold text-amber-950">
                {waitingChatCount}
              </span>
            )}
          </NavLink>
          <NavLink to="/profile" className={navItemClass}>
            <IconBuilding className={navIconClass} />
            <span>Profile</span>
          </NavLink>
          <NavLink to="/custom-fields" className={navItemClass}>
            <IconActivity className={navIconClass} />
            <span>Custom Fields</span>
          </NavLink>
          <NavLink to="/enquiries" className={navItemClass}>
            <IconChat className={navIconClass} />
            <span>Enquiries</span>
          </NavLink>
          <NavLink to="/registrations" className={navItemClass}>
            <IconUsers className={navIconClass} />
            <span>Registrations</span>
          </NavLink>
          <NavLink to="/projects" className={navItemClass}>
            <IconBuilding className={navIconClass} />
            <span>Projects</span>
          </NavLink>
          <NavLink to="/brands" className={navItemClass}>
            <IconBuilding className={navIconClass} />
            <span>Brands</span>
          </NavLink>
          <NavLink to="/product-categories" className={navItemClass}>
            <IconArticle className={navIconClass} />
            <span>Product Categories</span>
          </NavLink>
          <NavLink to="/products" className={navItemClass}>
            <IconArticle className={navIconClass} />
            <span>Products</span>
          </NavLink>
          <NavLink to="/services" className={navItemClass}>
            <IconArticle className={navIconClass} />
            <span>Services</span>
          </NavLink>
          <NavLink to="/product-enquiries" className={navItemClass}>
            <IconChat className={navIconClass} />
            <span>Product Enquiries</span>
          </NavLink>
          <NavLink to="/testimonials" className={navItemClass}>
            <IconUsers className={navIconClass} />
            <span>Testimonials</span>
          </NavLink>
          <NavLink to="/galleries" className={navItemClass}>
            <IconActivity className={navIconClass} />
            <span>Galleries</span>
          </NavLink>
          <NavLink to="/media" className={navItemClass}>
            <IconArticle className={navIconClass} />
            <span>Media</span>
          </NavLink>
          <NavLink to="/blogs" className={navItemClass}>
            <IconArticle className={navIconClass} />
            <span>Blogs</span>
          </NavLink>
          <NavLink to="/web-activity" className={navItemClass}>
            <IconActivity className={navIconClass} />
            <span>Web Activity</span>
          </NavLink>
        </nav>
      </aside>

      <div className="ml-64 flex h-screen min-h-0 min-w-0 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white/80 px-6 backdrop-blur">
          <h1 className="text-base font-semibold tracking-tight text-slate-900 sm:text-lg">Ainsoft CMS Portal</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openChangePassword}
              className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300/70"
            >
              Change password
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-400/50"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Logout
            </button>
          </div>
        </header>
        <main className="min-h-0 w-full flex-1 overflow-auto p-4 sm:p-6 md:p-8">
          <Outlet />
        </main>
      </div>

      {waitingChatCount > 0 && (
        <div className="fixed right-6 top-6 z-[90] w-[min(92vw,380px)] overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-2xl shadow-amber-950/20">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 text-white">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20 ring-1 ring-white/30">
                <IconChat className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold uppercase tracking-wide">Incoming Live Chat</p>
                <p className="mt-1 text-sm text-white/90">
                  {waitingChatCount} {waitingChatCount === 1 ? "visitor is" : "visitors are"} waiting for support.
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-3 p-5">
            <p className="text-sm text-slate-600">
              The alert will keep ringing until the chat is accepted from the Live Chat screen.
            </p>
            <button
              type="button"
              onClick={() => void acceptWaitingChat()}
              disabled={acceptingChat}
              className="w-full rounded-xl bg-brand-800 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600/40 disabled:opacity-60"
            >
              {acceptingChat ? "Accepting..." : "Accept Chat"}
            </button>
          </div>
        </div>
      )}

      {changePasswordOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="presentation">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setChangePasswordOpen(false)}
            aria-label="Close"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-password-title"
            className="relative z-10 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="change-password-title" className="text-lg font-semibold text-slate-900">Change password</h2>
            <p className="mt-1 text-sm text-slate-600">Update password for your current login.</p>
            <form onSubmit={runChangePassword} className="mt-4 space-y-3">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Current password *</label>
                <input
                  type="password"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">New password *</label>
                <input
                  type="password"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Confirm new password *</label>
                <input
                  type="password"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                />
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setChangePasswordOpen(false)}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
                >
                  {changingPassword ? "Updating..." : "Update password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
